import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../store/store';
import { API_ENDPOINTS } from '../api/endpoints';
import { HEADER_MESSAGE_TYPES } from '../utils/constants/header';

const ERROR_COPY = {
  400: 'Could not start bulk reveal - invalid request.',
  402: 'Insufficient enrichment credits.',
  502: 'Enrichment temporarily unavailable - try again later.',
  UNKNOWN: 'Could not start bulk reveal. Please try again.'
};

/**
 * Wraps `POST /enrichment/bulk-reveal`. On 202, surfaces `jobId` for the
 * caller to feed into `useEnrichmentJobPolling`. Skipped contacts and
 * estimated cost are exposed for downstream UI (collapsed summary,
 * post-completion ledger comparison).
 *
 * BE response shape (controllers/enrichment.js:264, verified 2026-05-23):
 *   202 → { jobId, status, totalContacts, accepted, skipped, estimatedCost,
 *           surfeEnrichmentId }
 *   400 → invalid request (contactIds/fields missing or unsupported)
 *   402 → insufficient credits (with { required, available, skipped })
 *   502 → surfe provider failure
 *
 * Pre-call cost-vs-balance + overage-modal gating is the caller's
 * responsibility — this hook only fires the request.
 *
 * @param {Object} [params]
 * @param {Function} [params.refetchAccess] - useEnrichmentAccess().refetch from
 *   the caller. TD-2100 AC: a 402 must refetch subscription status so the badge
 *   reflects the BE's authoritative remaining-credit count. Taken as an argument
 *   rather than mounting an access hook here, which would fetch
 *   /subscription-status on every mount to serve one error branch.
 */
const useBulkReveal = ({ refetchAccess } = {}) => {
  const store = useStore();
  const [jobId, setJobId] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [estimatedCost, setEstimatedCost] = useState(0);
  // TD-2357: identity → minted contact_enrichment_id map returned when the
  // request includes ephemeral discovered contacts. Exposed so the caller can
  // reconcile its ephemeral cards (the completion refetch also replaces them).
  const [resolved, setResolved] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMounted = useRef(true);
  useEffect(() => {
    // Re-arm on mount so a remount (Strict Mode double-invoke, list-key reuse)
    // doesn't leave the ref stuck at false from a previous unmount.
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const toast = useCallback(
    (message) => store.utilsStore?.setHeaderMessage?.(message, HEADER_MESSAGE_TYPES.ERROR),
    [store]
  );

  const triggerBulkReveal = useCallback(
    ({ contactIds, discoveredContacts, profileId, fields, companyId, skipPreviouslyEnriched }) => {
      const storedIds = Array.isArray(contactIds) ? contactIds : [];
      const discovered = Array.isArray(discoveredContacts) ? discoveredContacts : [];
      // TD-2357: fire when EITHER stored ids OR ephemeral discovered identities
      // are present (a discovered-only bulk reveal is valid).
      if (storedIds.length === 0 && discovered.length === 0) return;
      if (!Array.isArray(fields) || fields.length === 0) return;
      setIsSubmitting(true);
      store.makeRequest({
        endpoint: API_ENDPOINTS.ENRICHMENT_BULK_REVEAL,
        // companyId lets the BE derive the company name for the (company-scoped)
        // bulk reveal — omit when unknown.
        // TD-2312: forward the skip toggle so a skip-OFF reveal triggers the BE
        // forced re-fetch (re-bills already found/not_found). Absent → BE
        // defaults to skip-ON, preserving prior behaviour.
        body: {
          contactIds: storedIds,
          fields,
          // TD-2357: ephemeral discovered identities + the CREFO profile id they
          // are minted under, sent ONLY when present so a stored-only request is
          // byte-identical to before (BE requires profileId with discovered[]).
          ...(discovered.length ? { discoveredContacts: discovered, profileId } : {}),
          ...(companyId === undefined ? {} : { companyId }),
          ...(skipPreviouslyEnriched === undefined ? {} : { skipPreviouslyEnriched })
        },
        onSuccess: (data) => {
          if (!isMounted.current) return;
          setJobId(data?.jobId ?? null);
          setSkipped(Array.isArray(data?.skipped) ? data.skipped : []);
          setEstimatedCost(typeof data?.estimatedCost === 'number' ? data.estimatedCost : 0);
          // TD-2357: identity → minted contact_enrichment_id map (present only
          // for a mixed / discovered reveal).
          setResolved(Array.isArray(data?.resolved) ? data.resolved : []);
          setIsSubmitting(false);
        },
        onError: (_msg, response) => {
          if (!isMounted.current) return;
          // store.makeRequest passes the full response object; HTTP status is on
          // `response.httpStatus` (api.js passthrough).
          const statusCode = response?.httpStatus;
          setIsSubmitting(false);
          toast(ERROR_COPY[statusCode] || ERROR_COPY.UNKNOWN);
          // TD-2100: refresh the credit balance on 402 so badge flips to grey.
          if (statusCode === 402) refetchAccess?.();
        }
      });
    },
    [store, toast, refetchAccess]
  );

  const resetBulkReveal = useCallback(() => {
    setJobId(null);
    setSkipped([]);
    setEstimatedCost(0);
    setResolved([]);
  }, []);

  return {
    jobId,
    skipped,
    estimatedCost,
    resolved,
    isSubmitting,
    triggerBulkReveal,
    resetBulkReveal
  };
};

export default useBulkReveal;
